///
/// EarthBrowser
///
/// Created by Matt Giger on 8/21/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type ray } from "./ray"
import { type vec3 } from "./vector"

/**
 * Represents a plane in 3D space defined by the equation ax + by + cz + d = 0.
 */
export class plane {
	/** The x-coefficient of the plane equation */
	public readonly a: number
	/** The y-coefficient of the plane equation */
	public readonly b: number
	/** The z-coefficient of the plane equation */
	public readonly c: number
	/** The constant term of the plane equation */
	public readonly d: number

	/**
	 * Creates a new plane instance.
	 * @param a - The x-coefficient of the plane equation
	 * @param b - The y-coefficient of the plane equation
	 * @param c - The z-coefficient of the plane equation
	 * @param d - The constant term of the plane equation
	 */
	constructor(a: number, b: number, c: number, d: number) {
		this.a = a
		this.b = b
		this.c = c
		this.d = d
	}

	/**
	 * Calculates the signed distance from a point to the plane.
	 * @param v - The point to calculate the distance from
	 * @returns The signed distance from the point to the plane
	 */
	public distance(v: vec3): number {
		return (this.a*v.x + this.b*v.y + this.c*v.z) + this.d
	}

	/**
	 * Returns a normalized version of the plane.
	 * @returns A new plane instance with normalized coefficients
	 */
	public normalized(): plane  {
		const len = Math.sqrt(this.a*this.a + this.b*this.b + this.c*this.c)
		return new plane(this.a/len, this.b/len, this.c/len, this.d/len)
	}

	/**
	 * Checks if a ray intersects the plane.
	 * @param r - The ray to check for intersection
	 * @returns True if the ray intersects the plane, false otherwise
	 */
	public intersects(r: ray): boolean {
		return Math.abs((this.a * r.direction.x + this.b * r.direction.y + this.c * r.direction.z)) > Number.EPSILON
	}

	/**
	 * Calculates the distance along the ray to the intersection point with the plane.
	 * @param r - The ray to calculate the intersection distance for
	 * @returns The distance to the intersection point, or -1 if no intersection
	 */
	public intersect_distance(r: ray): number{
		const denom = this.a * r.direction.x + this.b * r.direction.y + this.c * r.direction.z
		return (Math.abs(denom) > Number.EPSILON) ? -(this.a * r.origin.x + this.b * r.origin.y + this.c * r.origin.z + this.d) / denom : -1
	}

	/**
	 * Calculates the intersection point of a ray with the plane.
	 * @param r - The ray to calculate the intersection point for
	 * @returns The intersection point as a vec3
	 */
	public intersection_point(r: ray): vec3 {
		return r.pointAt(this.intersect_distance(r))
	}
}